// Public form fill page (PRD §4.13). No auth — anyone with the link can fill it.
// Fetches the definition from the publicForm endpoint and posts the submission.
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { fetchPublicForm, submitPublicForm, type FormField } from '../lib/forms';

const inputCls = 'w-full rounded-[9px] border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary';
const typeAttr: Record<string, string> = { text: 'text', phone: 'tel', email: 'email', number: 'number' };

export default function FormFill() {
  const { t } = useI18n();
  const { tenantId = '', formId = '' } = useParams();
  const [name, setName] = useState('');
  const [fields, setFields] = useState<FormField[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [state, setState] = useState<'loading' | 'ready' | 'error' | 'done'>('loading');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchPublicForm(tenantId, formId)
      .then((d) => { setName(d.name); setFields(d.fields); setState('ready'); })
      .catch((e) => { setError((e as Error).message); setState('error'); });
  }, [tenantId, formId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    try { await submitPublicForm(tenantId, formId, values); setState('done'); }
    catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-surface px-4 py-10">
      <div className="mx-auto w-full max-w-md rounded-card border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-[8px] bg-primary text-sm font-extrabold text-white">W</span>
          <b className="text-sm text-ink-3">WaTag</b>
        </div>

        {state === 'loading' && <p className="py-8 text-center text-sm text-ink-3">…</p>}
        {state === 'error' && <p className="py-8 text-center text-sm font-semibold text-red-600">{error || t('form.unavailable') || 'This form is unavailable.'}</p>}
        {state === 'done' && (
          <div className="py-8 text-center">
            <div className="text-4xl">✅</div>
            <p className="mt-3 text-lg font-extrabold text-ink">{t('form.thankYou') || 'Thank you!'}</p>
            <p className="mt-1 text-sm text-ink-3">{t('form.recorded') || 'Your response has been recorded.'}</p>
          </div>
        )}
        {state === 'ready' && (
          <form onSubmit={submit} className="space-y-4">
            <h1 className="text-xl font-extrabold text-ink">{name}</h1>
            {fields.map((f) => (
              <label key={f.id} className="block">
                <span className="mb-1 block text-sm font-semibold text-ink">{f.label}{f.required && <span className="text-red-500"> *</span>}</span>
                <input
                  type={typeAttr[f.type] ?? 'text'}
                  required={f.required}
                  value={values[f.id] ?? ''}
                  onChange={(e) => setValues({ ...values, [f.id]: e.target.value })}
                  className={inputCls}
                />
              </label>
            ))}
            {error && <p className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{error}</p>}
            <button type="submit" disabled={busy} className="w-full rounded-[9px] bg-primary px-4 py-3 text-sm font-bold text-white disabled:opacity-50">
              {busy ? '…' : t('form.submit') || 'Submit'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
